import { useState, useEffect, ChangeEvent } from 'react';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useSolana } from '../../hooks/useSolana';
import '../../VolumeWalletControl.css';
import { useRaydium } from '../../hooks/useRaydium';
import { LiquidityPoolKeysV4, WSOL } from '@raydium-io/raydium-sdk';
import { getMint } from '@solana/spl-token';
import { useSessionGateway } from '../../hooks/useSessionGateway';
import { REMOTE_REMOVE_100_LP_TOKEN, REMOTE_REMOVE_99_9_LP_TOKEN, REMOTE_VOLUME_BUYER_SWITCH, REMOTE_VOLUME_SELLER_SWITCH } from './RapidLaunch';
import { Api } from '@sodamnfoolish/spl-factory-backend-types';
import {
    volumeWalletControlJitoTipDefault,
    volumeWalletControlMaxPriorityFeeJitoLamports,
    volumeWalletControlMaxPriorityFeeRPCLamports,
    volumeWalletControlMinPriorityFeeJitoLamports,
    volumeWalletControlMinPriorityFeeRPCLamports,
} from '../../../ConfigFees';

export function VolumeWalletControl({
    volumeBuyWallets,
    connection,
    deployedToken,
    logMessage,
    injectToken,
    updatedAt,
    autoAction,
    receivePoolKeys,
    poolKeys,
}: {
    volumeBuyWallets: Keypair[];
    connection: Connection;
    deployedToken: string;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    injectToken: (address: string) => Promise<void>;
    updatedAt: string;
    autoAction: string;
    receivePoolKeys: () => Promise<LiquidityPoolKeysV4>;
    poolKeys: LiquidityPoolKeysV4 | undefined;
}) {
    const { getMultipleSolBalances } = useSolana();
    const { getWalletsTokenStringBalances } = useSplToken();
    const { swap } = useRaydium();
    // const { automatizationClient } = useAutomatizationGateway((role) => role == Role.Seller);
    const { sessionClient } = useSessionGateway();

    const [selectedWalletsCount, setSelectedWalletsCount] = useState<number>(0);
    const [selectedWallets, setSelectedWallets] = useState<boolean[]>(new Array(volumeBuyWallets.length).fill(false));
    const [jitoTip, setJitoTip] = useState<number>(volumeWalletControlJitoTipDefault);
    const [randomWalletsInput, setRandomWalletsInput] = useState<number>(1);
    const [solBalances, setSolBalances] = useState<number[]>(new Array(volumeBuyWallets.length).fill(0));
    const [initialTotalSolBalance, setInitialTotalSolBalance] = useState<number>(0);
    const [totalSolBalance, setTotalSolBalance] = useState<number>(0);
    const [solProfit, setSolProfit] = useState<number>(0);
    const [tokenBalances, setTokenBalances] = useState<number[]>([]);
    const [totalTokenBalance, setTotalTokenBalance] = useState<number>(0);
    const [tokenSupplyPercent, setTokenSupplyPercent] = useState<number>(0);

    const [customAmountToBuy, setCustomAmountToBuy] = useState<number>(0.02);

    const [randomSellLock0, setRandomSellLock0] = useState(false);
    const [randomSellInterval0, setRandomSellInterval0] = useState<number>(1);
    const [randomSellPercent0, setRandomSellPercent0] = useState<number>(100);
    const [randomSellIntervalTimer0, setRandomSellIntervalTimer0] = useState<NodeJS.Timer | null>(null);

    const [randomSellLock1, setRandomSellLock1] = useState(false);
    const [randomSellInterval1, setRandomSellInterval1] = useState<number>(5);
    const [randomSellPercent1, setRandomSellPercent1] = useState<number>(100);
    const [randomSellIntervalTimer1, setRandomSellIntervalTimer1] = useState<NodeJS.Timer | null>(null);

    const [jitoTransaction, setJitoTransaction] = useState<boolean>(true);
    // useEffect(() => {
    //     if (!automatizationClient) return;
    //
    //     automatizationClient.on(Event.PreStart, async (address: string, callback: ClientCallback) => {
    //         await injectToken(address).catch((reason) => {
    //             callback([new ClientLog(LogLevel.Error, `Error`)]);
    //             throw reason;
    //         });
    //         callback([new ClientLog(LogLevel.Info, `Address received ${address}`)]);
    //     });
    //
    //     automatizationClient.on(Event.StartTrading, async (address: string, callback: ClientCallback) => {
    //         if (randomSellIntervalTimer0 == null)
    //             await switchRandomSell0().catch((reason) => {
    //                 callback([new ServerLog(LogLevel.Error, 'Error')]);
    //                 throw reason;
    //             });
    //
    //         setTimeout(() => callback([new ServerLog(LogLevel.Info, 'Started')]));
    //     });
    //
    //     automatizationClient.on(Event.StopTrading, (address: string, callback: ClientCallback) => {
    //         callback([new ClientLog(LogLevel.Info, 'Stopped')]);
    //         window.location.reload();
    //     });
    //
    //     return () => {
    //         automatizationClient.off(Event.PreStart);
    //     };
    // }, [automatizationClient]);

    useEffect(() => {
        if (!sessionClient) return;

        // TODO: change logic on backend to use roles (pages)

        sessionClient.on(REMOTE_VOLUME_BUYER_SWITCH, (_: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_REMOVE_99_9_LP_TOKEN, (callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_REMOVE_100_LP_TOKEN, (callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_VOLUME_SELLER_SWITCH, async (enabled: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            if (!deployedToken) {
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Seller mint not provided')]);
                return;
            }

            const isEnabled = () => randomSellIntervalTimer0 != null;

            try {
                if (isEnabled() == enabled) {
                    callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Warn, 'Seller already switched')]);
                    return;
                }

                await new Promise<void>((resolve, reject) => {
                    switchRandomSell0().catch(reject);
                    setTimeout(resolve, 1000);
                });

                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Info, 'Seller switched')]);
            } catch (error) {
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Seller error')]);
                throw error;
            }
        });

        return () => {
            sessionClient.off(REMOTE_VOLUME_SELLER_SWITCH);
            sessionClient.off(REMOTE_REMOVE_99_9_LP_TOKEN);
            sessionClient.off(REMOTE_REMOVE_100_LP_TOKEN);
            sessionClient.off(REMOTE_VOLUME_BUYER_SWITCH);
        };
    }, [sessionClient, deployedToken, randomSellIntervalTimer0]);

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);

    useEffect(() => {
        if (!deployedToken) return;
        if (autoAction !== 'volumeSell') return;
        if (autoActionStarted) return;
        if (!poolKeys) return;

        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (timeDifference > 30000) return;

        logMessage('Starting auto action for Volume Wallet Control.');
        setAutoActionStarted(true);
        switchRandomSell0();
    }, [deployedToken, autoAction, autoActionStarted, poolKeys]);

    const selectedWalletsChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const newSelectedWallets = [...selectedWallets];
        newSelectedWallets[index] = event.target.checked;
        setSelectedWallets(newSelectedWallets);
    };

    const jitoTipChange = (event: ChangeEvent<HTMLInputElement>) => {
        setJitoTip(Number(event.target.value));
    };

    const randomWalletsInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        let walletsNumber = Number(event.target.value);
        const positiveBalances = tokenBalances.filter((balance) => balance > 0).length;
        if (walletsNumber > positiveBalances) {
            walletsNumber = positiveBalances;
        }
        setRandomWalletsInput(walletsNumber);
    };

    useEffect(() => {
        const fetchBalances = async () => {
            try {
                const allSolBalances = await getMultipleSolBalances(
                    connection,
                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                );
                setSolBalances(allSolBalances);

                const totalSolBalance = Number(allSolBalances.reduce((acc, balance) => acc + balance, 0) / LAMPORTS_PER_SOL);
                setTotalSolBalance(totalSolBalance);

                if (initialTotalSolBalance === 0) {
                    setInitialTotalSolBalance(totalSolBalance);
                } else {
                    const totalProfit = (totalSolBalance - initialTotalSolBalance) / LAMPORTS_PER_SOL;
                    setSolProfit(Number(totalProfit));
                }

                if (!deployedToken || !volumeBuyWallets.length) {
                    return setTokenBalances(new Array(volumeBuyWallets.length).fill('-'));
                }
                console.log('deployedToken', deployedToken);
                const token = await getMint(connection, new PublicKey(deployedToken), 'processed');

                const allTokenBalances = await getWalletsTokenStringBalances(
                    connection,
                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                    new PublicKey(deployedToken),
                );
                console.log(Number(allTokenBalances[0]) / 10 ** token.decimals);
                setTokenBalances(allTokenBalances.map((balance) => Number(balance) / 10 ** token.decimals));

                const totalTokenBalance = Number(allTokenBalances.reduce((acc, balance) => acc + Number(balance), 0));
                setTotalTokenBalance(totalTokenBalance / 10 ** token.decimals);

                const getTokenSupplyPercent = async () => {
                    const supply = Number(token.supply.toString());
                    const supplyRatio = totalTokenBalance / supply;
                    const percent = supplyRatio * 100;

                    setTokenSupplyPercent(percent);
                };
                getTokenSupplyPercent();
            } catch (error) {
                console.error(error);
            }
        };

        fetchBalances();

        const intervalId = setInterval(fetchBalances, 20000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeBuyWallets]);

    const switchRandomSell0 = async () => {
        if (randomSellLock0) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock0(true);

        if (randomSellIntervalTimer0) {
            clearInterval(randomSellIntervalTimer0);
            setRandomSellIntervalTimer0(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            logMessage('Searching for AMM pool...');
            let keys = await receivePoolKeys();

            setRandomSellIntervalTimer0(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            volumeBuyWallets.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], keys, [randomSellPercent0]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval0 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock0(false);
    };

    const switchRandomSell1 = async () => {
        if (randomSellLock1) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock1(true);

        if (randomSellIntervalTimer1) {
            clearInterval(randomSellIntervalTimer1);
            setRandomSellIntervalTimer1(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            logMessage('Searching for AMM pool...');
            let keys = await receivePoolKeys();
            setRandomSellIntervalTimer1(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            volumeBuyWallets.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], keys, [randomSellPercent1]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval1 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock1(false);
    };

    useEffect(() => {
        const count = selectedWallets.filter(Boolean).length;
        setSelectedWalletsCount(count);
    }, [selectedWallets]);

    function selectRandomWallets() {
        const randomWallets: number[] = [];
        const totalWallets = volumeBuyWallets.length;

        const positiveBalances = tokenBalances.filter((balance) => balance > 0).length;
        console.log(positiveBalances);
        if (randomWalletsInput > positiveBalances) {
            logMessage('Input cannot be greater than the number of available wallets.', 'error');
            return;
        }

        while (randomWallets.length < randomWalletsInput) {
            const randomIndex = Math.floor(Math.random() * totalWallets);
            if (!randomWallets.includes(randomIndex) && tokenBalances[randomIndex] > 0) {
                randomWallets.push(randomIndex);
            }
        }

        const newSelectedWallets = new Array(totalWallets).fill(false);
        randomWallets.forEach((index) => {
            newSelectedWallets[index] = true;
        });
        setSelectedWallets(newSelectedWallets);
    }

    async function buyMultiple(connection: Connection, wallets: Keypair[], poolKeys: LiquidityPoolKeysV4, solAmounts: number[]) {
        logMessage('Buying...');
        for (const [index, wallet] of wallets.entries()) {
            buySingle(connection, wallet, solAmounts[index], poolKeys);
        }
    }

    async function buySingle(connection: Connection, wallet: Keypair, solAmount: number, poolKeys: LiquidityPoolKeysV4) {
        try {
            const min = jitoTransaction ? volumeWalletControlMinPriorityFeeJitoLamports : volumeWalletControlMinPriorityFeeRPCLamports;
            const max = jitoTransaction ? volumeWalletControlMaxPriorityFeeJitoLamports : volumeWalletControlMaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);
            await swap(
                connection,
                wallet,
                wallet,
                poolKeys,
                solAmount,
                1,
                new PublicKey(WSOL.mint),
                new PublicKey(deployedToken),
                priorityFeeLamports,
                jitoTip * LAMPORTS_PER_SOL,
                logMessage,
                true,
                jitoTransaction,
            );
            logMessage(`Bought ${(solAmount / LAMPORTS_PER_SOL).toFixed(3)} SOL from ${wallet.publicKey.toBase58()}`);
        } catch {
            logMessage(`Error buying wallet from ${wallet.publicKey.toBase58()}`);
        }
    }

    async function sellMultipleWithPercent(connection: Connection, wallets: Keypair[], poolKeys: LiquidityPoolKeysV4, percents: number[]) {
        logMessage('Selling...');
        const balances = (
            await getWalletsTokenStringBalances(
                connection,
                wallets.map((wallet) => wallet.publicKey),
                new PublicKey(deployedToken),
            )
        ).map((balance) => Number(balance));

        const swapValues = balances.map((balance, index) => {
            return Math.floor((balance * percents[index]) / 100);
        });

        for (const [index, wallet] of wallets.entries()) {
            sellSingle(connection, wallet, swapValues[index], poolKeys);
        }
    }

    async function sellSingle(connection: Connection, wallet: Keypair, tokenAmount: number, poolKeys: LiquidityPoolKeysV4) {
        try {
            const min = jitoTransaction ? volumeWalletControlMinPriorityFeeJitoLamports : volumeWalletControlMinPriorityFeeRPCLamports;
            const max = jitoTransaction ? volumeWalletControlMaxPriorityFeeJitoLamports : volumeWalletControlMaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);

            await swap(
                connection,
                wallet,
                wallet,
                poolKeys,
                tokenAmount,
                1,
                new PublicKey(deployedToken),
                new PublicKey(WSOL.mint),
                priorityFeeLamports,
                jitoTip * LAMPORTS_PER_SOL,
                logMessage,
                true,
                jitoTransaction,
            );
            logMessage(`Sold ${tokenAmount} tokens from ${wallet.publicKey.toBase58()}`);
        } catch {
            logMessage(`Error selling wallet from ${wallet.publicKey.toBase58()}`);
        }
    }
    return (
        <section>
            <h2>Volume Wallet Control</h2>
            <section className='volume-sell-buy-container'>
                <div className='volume-selection-container'>
                    <p>
                        <span>{selectedWalletsCount}</span> Wallet(s) Selected
                    </p>
                    <div className='jito-tx-check-inp'>
                        <input type='checkbox' checked={jitoTransaction} onClick={() => setJitoTransaction((prev) => !prev)} />
                        <p>Jito TX</p>
                    </div>
                    <label>
                        JITO Tip
                        <input type='number' value={jitoTip} onChange={jitoTipChange} />
                    </label>
                </div>
                <div className='volume-sell-buttons-container'>
                    <p>SELL</p>
                    <div>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(100),
                                );
                            }}
                        >
                            100%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(75),
                                );
                            }}
                        >
                            75%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(50),
                                );
                            }}
                        >
                            50%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(20),
                                );
                            }}
                        >
                            20%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(10),
                                );
                            }}
                        >
                            10%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(5),
                                );
                            }}
                        >
                            5%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(1),
                                );
                            }}
                        >
                            1%
                        </button>
                    </div>
                </div>
                <div className='volume-buy-buttons-container'>
                    <p>BUY</p>
                    <div>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(0.001 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            0.001
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(0.1 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            0.1
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(0.25 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            0.25
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(0.5 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            0.5
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(0.75 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            0.75
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            1
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(1.5 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            1.5
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(2 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            2
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(5 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            5
                        </button>
                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(10 * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            10
                        </button>

                        <input type='number' value={customAmountToBuy} onChange={(event) => setCustomAmountToBuy(Number(event.target.value))} />

                        <button
                            onClick={async () => {
                                buyMultiple(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    await receivePoolKeys(),
                                    new Array(selectedWalletsCount).fill(customAmountToBuy * LAMPORTS_PER_SOL),
                                );
                            }}
                        >
                            Buy
                        </button>
                    </div>
                </div>
            </section>
            <section className='volume-control-buttons-container'>
                <section className='interval-ref-selrandom'>
                    <div className='enable-sec-percent'>
                        <div className='inp'>
                            <label className='switch'>
                                <input
                                    type='checkbox'
                                    checked={randomSellIntervalTimer0 != null}
                                    onClick={switchRandomSell0}
                                    disabled={!deployedToken || volumeBuyWallets.length === 0}
                                />
                                <span className='slider'></span>
                            </label>
                        </div>

                        <div className='interval'>
                            <input
                                type={'number'}
                                value={randomSellInterval0}
                                onChange={(event) => setRandomSellInterval0(parseInt(event.target.value) || 5)}
                                disabled={randomSellIntervalTimer0 != null}
                            />
                            sec
                        </div>
                        <div className='percent'>
                            <input
                                type={'number'}
                                value={randomSellPercent0}
                                onChange={(event) => setRandomSellPercent0(parseInt(event.target.value) || 10)}
                                disabled={randomSellIntervalTimer0 != null}
                            />
                            %
                        </div>
                    </div>
                    <div className='volume-select-random-button'>
                        <button className='button-accent' onClick={selectRandomWallets}>
                            SELECT RANDOM &nbsp; &nbsp; &nbsp;
                            <input type='number' value={randomWalletsInput} onChange={randomWalletsInputChange} className='input-on-button' /> &nbsp; WALLETS
                        </button>
                    </div>
                </section>
                <br />
                <section className='ref-sel-all'>
                    <div className='enable-sec-percent'>
                        <div className='inp'>
                            <label className='switch'>
                                <input
                                    type='checkbox'
                                    checked={randomSellIntervalTimer1 != null}
                                    onClick={switchRandomSell1}
                                    disabled={!deployedToken || volumeBuyWallets.length === 0}
                                />
                                <span className='slider'></span>
                            </label>
                        </div>

                        <div className='interval'>
                            <input
                                type={'number'}
                                value={randomSellInterval1}
                                onChange={(event) => setRandomSellInterval1(parseInt(event.target.value) || 5)}
                                disabled={randomSellIntervalTimer1 != null}
                            />
                            sec
                        </div>
                        <div className='percent'>
                            <input
                                type={'number'}
                                value={randomSellPercent1}
                                onChange={(event) => setRandomSellPercent1(parseInt(event.target.value) || 10)}
                                disabled={randomSellIntervalTimer1 != null}
                            />
                            %
                        </div>
                    </div>
                    <div className='volume-control-buttons'>
                        <button className='button-accent'>
                            <div className='icon-ref'></div>
                        </button>
                        <button
                            className='button-accent'
                            onClick={() => setSelectedWallets(new Array(volumeBuyWallets.length).fill(selectedWalletsCount !== volumeBuyWallets.length))}
                        >
                            <div className='icon-sel-all'></div>
                        </button>
                    </div>
                </section>
            </section>
            <div className='home-table-wrapper'>
                <table className='volume-control-table'>
                    <thead>
                        <tr>
                            <th>Volume Wallet</th>
                            <th>Tokens</th>
                            <th>SOL</th>
                            <th>Select</th>
                        </tr>
                    </thead>
                    <tbody>
                        {volumeBuyWallets.map((wallet, index) => (
                            <tr key={index}>
                                <td className='volume-wallets'>
                                    <b>Vol{index + 1}</b> - {wallet.publicKey.toBase58().substring(0, 5)}...
                                    {wallet.publicKey.toBase58().substring(wallet.publicKey.toBase58().length - 5)}
                                </td>
                                <td>{tokenBalances[index]} </td>
                                <td>{(solBalances[index] / LAMPORTS_PER_SOL).toFixed(2)}</td>
                                <td>
                                    <input type='checkbox' checked={selectedWallets[index] || false} onChange={selectedWalletsChange(index)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td></td>
                            <td>
                                Total:
                                <br />
                                {totalTokenBalance.toLocaleString()}
                                <br />
                                {tokenSupplyPercent.toFixed(3)}% of Supply
                            </td>
                            <td>
                                Total:
                                <br />
                                {totalSolBalance.toFixed(2)} SOL
                                {solProfit ? (
                                    <p>
                                        <span className='sol-profit'>+{solProfit.toFixed(2)} SOL </span>
                                        Profit
                                    </p>
                                ) : (
                                    <p>
                                        <span className='no-sol-profit'>{solProfit.toFixed(2)} SOL </span>
                                        Profit
                                    </p>
                                )}
                            </td>
                        </tr>
                        <tr></tr>
                    </tfoot>
                </table>
            </div>
        </section>
    );
}
